import styled from 'styled-components';
import React, { useContext, useEffect, useState } from 'react';
import { Box, Image, Media } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';

import Context from '../service/Context';
import themeStyles from './styles/Details.styles';
import { ErrorStatus } from '../service/constants';
import { ModelExplorationContext } from '../../ModelExplorationContext';
import { getBapQueryStringVariables } from '../../../utils/urls';
import { getColorAccessoryOfferOrDefault } from '../../../utils/trimUtils';
import getPeAccessoryCreditOffers from './utils/getPeAccessoryCreditOffers';
import DesktopTrimDetails from './DesktopTrimDetails';
import MobileTrimDetails from './MobileTrimDetails';
import createCarouselImages from './utils/createCarouselImages';
import createPeCarouselImages from './utils/createPeCarouselImages';
import { useAppName } from '../../../utils/sitecoreContext';
import { PE_SITE_NAME } from '../../../utils/constants';
import DealerInventoryModal from './DealerInventoryModal';
import { TrimPackagesModalType } from '../service/ConfigurationProvider';
import gtmViewTrimDetails from '../../../core/gtm/gtmViewTrimDetails';
import usePriceFlagsForProvince from '../../../utils/hooks/usePriceFlagsForProvince';

const MissingLabel = themeStyles.apply(Box, 'MissingLabel');
const MissingImage = styled(Image)`
  opacity: 0.3;
`;

const getIsInteriorColorValid = (interiorKey, exteriorKey, exteriorColors) => {
  const selectedExteriorColorObject = exteriorColors.find(c => c.color.item?.fields?.detKey?.value === exteriorKey);
  const validInteriorColorKeys = selectedExteriorColorObject?.interiorColors.items.map(
    c => c.color.item?.fields?.detKey?.value,
  );

  return validInteriorColorKeys?.includes(interiorKey);
};

const getDefaultInteriorColor = (exteriorKey, exteriorColors) => {
  const exteriorColor = exteriorColors[0]?.colors.find(c => c.color.item?.fields?.detKey?.value === exteriorKey);
  const defaultInteriorColor = exteriorColor?.defaultInteriorColor?.item?.fields?.color;

  return defaultInteriorColor?.fields?.detKey?.value;
};

const Details = ({
  category,
  componentType,
  featuresPath,
  hasError,
  isFetching,
  isPackageSelectorOpen,
  isSellingPriceProvince,
  selectedExteriorColor,
  setSelectedExteriorColor,
  selectedInteriorColor,
  setSelectedInteriorColor,
  showInformationalApr,
  accessoryCreditOffers,
  showTrimName = false,
  trim,
  modelCategory,
}) => {
  const { vehicleType, paymentOptions, modal, setModal } = useContext(Context);
  const { selectedTransmission, setSelectedTransmission, bapUrlParams, setBapUrlParams, isDark, subCategory } =
    useContext(ModelExplorationContext) || {};
  const { showSellingPrice } = usePriceFlagsForProvince();

  const { t } = useTranslation();
  const appName = useAppName();
  const {
    defaultInteriorColorKey,
    defaultExteriorColorKey,
    defaultTransmission,
    trimKey,
    modelKey,
    modelYear,
    modelCode,
    transmissionModelCode,
  } = trim;

  useEffect(() => {
    if (isFetching) return;
    gtmViewTrimDetails({
      productCode: trim?.modelCode,
      productName: trim?.gtmTrimName,
      discountAmount: trim?.pricing?.discount?.priceDiscountAmount,
      category: modelCategory,
      category2: subCategory,
      price: showSellingPrice ? trim?.pricing?.sellingPrice : trim?.pricing?.msrp,
    });
  }, [isFetching]);

  const [selectedAssetType, setSelectedAssetType] = useState('exterior');
  const [selectedAssetCategory, setSelectedAssetCategory] = useState('studio');
  const [showInteriorError, setShowInteriorError] = useState(ErrorStatus.FALSE);

  const handleExteriorSelection = key => {
    const isValid = getIsInteriorColorValid(
      selectedInteriorColor,
      key,
      selectedTransmission?.exteriorColors[0]?.colors,
    );
    const defaultInteriorColor = getDefaultInteriorColor(key, selectedTransmission?.exteriorColors);
    setSelectedExteriorColor(key);
    setSelectedAssetType('exterior');
    setShowInteriorError(ErrorStatus.FALSE);

    if (!isValid) {
      setSelectedInteriorColor(defaultInteriorColor);
    }
  };

  const handleInteriorSelection = key => {
    const isValid = getIsInteriorColorValid(
      key,
      selectedExteriorColor,
      selectedTransmission?.exteriorColors[0]?.colors,
    );

    if (isValid) {
      setSelectedAssetType('interior');
      setSelectedInteriorColor(key);
      setShowInteriorError(ErrorStatus.FALSE);
    } else {
      setShowInteriorError(ErrorStatus.TRUE);
    }
  };

  // rough implementation of what a select handler will be without interiors
  const handleAvailableSelection = key => {
    setSelectedExteriorColor(key);
    setSelectedAssetType('exterior');
  };

  // Reset colours to default when trim changes
  useEffect(() => {
    setSelectedTransmission(defaultTransmission);
    setSelectedExteriorColor(defaultExteriorColorKey);
    setSelectedInteriorColor(defaultInteriorColorKey);
    setShowInteriorError(ErrorStatus.FALSE);
    setSelectedAssetType('exterior');
  }, [trimKey]);

  useEffect(() => {
    setSelectedExteriorColor(defaultExteriorColorKey);
    setSelectedInteriorColor(defaultInteriorColorKey);
  }, [selectedTransmission]);

  // Update BAP URL params based on selections
  useEffect(() => {
    if (setBapUrlParams && bapUrlParams) {
      setBapUrlParams(
        getBapQueryStringVariables(
          modelKey,
          modelYear,
          trimKey,
          transmissionModelCode,
          selectedExteriorColor,
          selectedInteriorColor,
        ),
      );
    }
  }, [
    modelKey,
    modelYear,
    trimKey,
    transmissionModelCode,
    selectedExteriorColor,
    selectedInteriorColor,
    setBapUrlParams,
    bapUrlParams,
    selectedTransmission,
  ]);

  const getInteriorExteriorImages = (selectedColor, transmission, type) => {
    if (!selectedColor || !transmission || !transmission[type] || !transmission[type][0]) return null;
    const colors = transmission[type]?.[0].colors;
    const colorImage = colors?.find(color => color?.color?.item?.fields?.detKey?.value === selectedColor);
    return {
      landscape: selectedColor ? colorImage?.landscapeAssets?.value?.images : null,
      studio: selectedColor ? colorImage?.studioAssets?.value?.images : null,
      threeSixty: selectedInteriorColor
        ? colors?.find(color => color?.color?.item?.fields?.detKey?.value === selectedInteriorColor)?.threeSixtyAssets
            ?.value?.images
        : null,
    };
  };

  const interiorImages = getInteriorExteriorImages(selectedInteriorColor, selectedTransmission, 'interiorColors');
  const exteriorImages = getInteriorExteriorImages(selectedExteriorColor, selectedTransmission, 'exteriorColors');

  const exteriorColorSwatches = selectedTransmission?.exteriorColors?.[0]?.colors.map(c => ({
    name: c.color.item?.fields.colorName?.value || c.color.item?.displayName,
    gtmName: c.name?.toLowerCase(),
    color: c.color.item?.fields?.hexValue?.value,
    key: c.color.item?.fields.detKey.value,
    isSelected: c.color.item?.fields.detKey.value === selectedExteriorColor,
    isValid: true,
  }));

  const selectedExteriorColorItem =
    selectedTransmission?.exteriorColors?.[0]?.colors?.find(
      c => c.color.item?.fields.detKey.value === selectedExteriorColor,
    ) || {};

  const { firstOffer, secondOffer, thirdOffer } = selectedExteriorColorItem;
  const colorOffers = {
    firstOffer: firstOffer?.value,
    secondOffer: secondOffer?.value,
    thirdOffer: thirdOffer?.value,
  };

  const colorAccessoryCreditOffers =
    appName === PE_SITE_NAME
      ? getPeAccessoryCreditOffers(trim)
      : getColorAccessoryOfferOrDefault(colorOffers, accessoryCreditOffers);

  const interiorColorSwatches = selectedTransmission?.interiorColors?.[0]?.colors.map(c => ({
    name: c.color.item?.fields.colorName.value,
    gtmName: c.name?.toLowerCase(),
    color: c.color.item?.fields.hexValue?.value,
    key: c.color.item?.fields.detKey.value,
    isSelected: c.color.item?.fields.detKey.value === selectedInteriorColor,
    image: <Image {...c.color.item?.fields?.swatch?.value} />,
    isValid: getIsInteriorColorValid(
      c.color.item?.fields.detKey.value,
      selectedExteriorColor,
      selectedTransmission?.exteriorColors[0]?.colors,
    ),
  }));

  const { carouselImages, hasImages, missingAssetPlaceholderImage } =
    // TODO: hard coded variant not ideal, to be updated
    appName === PE_SITE_NAME
      ? createPeCarouselImages(trim)
      : createCarouselImages({
          selectedAssetType,
          interiorImages,
          selectedAssetCategory,
          exteriorImages,
        });

  const carouselItems = hasImages
    ? carouselImages.map(image => (
        <Image key={image?.alt?.value} disableObjectFit coverContainer src={image?.url} alt={image?.alt?.value} />
      ))
    : [
        <>
          <MissingImage
            disableObjectFit
            coverContainer
            src={missingAssetPlaceholderImage?.url}
            alt={missingAssetPlaceholderImage?.alt?.value}
          />
          <MissingLabel size="pricing" isDark={isDark}>
            {t('Pages.Models.Exploration.imageComingSoonLabel')}
          </MissingLabel>
        </>,
      ];

  const paymentDetailsProps = {
    isSellingPriceProvince,
    paymentOptions,
    showInformationalApr,
    trim: { exteriorColorKey: selectedExteriorColor, ...trim },
    vehicleType,
  };
  const showAssetTypeToggle =
    exteriorImages?.[selectedAssetCategory]?.length > 0 || interiorImages?.[selectedAssetCategory]?.length > 0;

  const assetsByType = selectedAssetType === 'interior' ? interiorImages : exteriorImages;
  const showStudioTab =
    assetsByType?.studio?.length > 0 && (assetsByType?.landscape?.length > 0 || assetsByType?.threeSixty?.length > 0);
  const showLandscapeTab =
    assetsByType?.landscape?.length > 0 && (assetsByType?.studio?.length > 0 || assetsByType?.threeSixty?.length > 0);
  const showThreeSixtyTab =
    assetsByType?.threeSixty?.length > 0 && (assetsByType?.studio?.length > 0 || assetsByType?.landscape?.length > 0);

  if (isPackageSelectorOpen) return null;

  const commonTrimDetailsProps = {
    componentType,
    trim,
    modelCategory,
    featuresPath,
    hasError,
    isFetching,
    paymentDetailsProps,
    isSellingPriceProvince,
    showInformationalApr,
    selectedExteriorColor,
    accessoryCreditOffers: colorAccessoryCreditOffers,
    hasImages,
    missingAssetPlaceholderImage,
    category,
    selectedAssetType,
    setSelectedAssetType,
    selectedAssetCategory,
    setSelectedAssetCategory,
    carouselItems,
    showAssetTypeToggle,
    exteriorColorSwatches,
    interiorColorSwatches,
    showInteriorError,
    handleExteriorSelection,
    handleInteriorSelection,
    setShowInteriorError,
    handleAvailableSelection,
  };

  const gtmTagging = {
    gtmTrimName: trim?.gtmTrimName,
    gtmCategoryName: category.toLowerCase(),
    gtmBodyStyle: trim?.gtmBodyStyle,
    price: showSellingPrice ? trim?.pricing?.sellingPrice : trim?.pricing?.msrp,
    discountAmount: trim?.pricing?.discount?.priceDiscountAmount,
  };

  return (
    <>
      <Media lessThan="desktop">
        <MobileTrimDetails
          showTrimName={showTrimName}
          selectedInteriorColor={selectedInteriorColor}
          {...commonTrimDetailsProps}
        />
      </Media>
      <Media greaterThanOrEqual="desktop">
        <DesktopTrimDetails
          showLandscapeTab={showLandscapeTab}
          showStudioTab={showStudioTab}
          showThreeSixtyTab={showThreeSixtyTab}
          {...commonTrimDetailsProps}
        />
      </Media>

      <DealerInventoryModal
        showModal={modal === TrimPackagesModalType.BUY_NOW_MODAL}
        closeModal={() => setModal(null)}
        modelCode={modelCode}
        gtmTagging={gtmTagging}
      />
    </>
  );
};

export default Details;
